import { EMBED_PROTOCOL, EMBED_VERSION, EmbedCommand, RequestId, type CommandName, type EmbedResponse, type EventData, type EventName, type GameState } from './protocol.ts';
import { TokenBucket } from './rateLimit.ts';

/** Minimal window surface (real Window in the browser, fakes in tests). */
export interface MessageWindow {
  postMessage(message: unknown, targetOrigin: string): void;
}
export interface MessageSource {
  addEventListener(type: 'message', h: (e: MessageEvent) => void): void;
  removeEventListener(type: 'message', h: (e: MessageEvent) => void): void;
}

export type CommandHandlers = {
  [K in CommandName]: (args: Extract<EmbedCommand, { command: K }> extends { args: infer A } ? A : undefined) => Promise<GameState | null> | GameState | null;
};

export interface GameBridgeOptions {
  self: MessageSource;
  parent: MessageWindow;
  /** Exact origin of the operator page (from the launch session allowlist). Never "*". */
  parentOrigin: string;
  handlers: CommandHandlers;
  now?: () => number;
  /** Burst / sustained command rate. */
  rate?: { capacity: number; perSecond: number };
  onDrop?: (reason: 'ORIGIN' | 'SOURCE' | 'NOT_PROTOCOL') => void;
}

export interface GameBridge {
  emit<E extends EventName>(event: E, data: EventData<E>): void;
  dispose(): void;
  readonly dropped: { origin: number; source: number };
}

/**
 * Game side of the embed protocol. Accepts only messages whose origin is exactly the configured parent
 * origin AND whose source is the parent window; validates the versioned schema; rate-limits; answers
 * every accepted command with its requestId. Everything is posted to the exact parent origin.
 */
export function createGameBridge(o: GameBridgeOptions): GameBridge {
  if (!/^https?:\/\/[^/*]+$/.test(o.parentOrigin)) throw new Error('parentOrigin must be an exact origin (no wildcard, no path)');
  const now = o.now ?? (() => Date.now());
  const bucket = new TokenBucket(o.rate?.capacity ?? 10, o.rate?.perSecond ?? 5, now);
  const dropped = { origin: 0, source: 0 };
  let seq = 0;
  const post = (m: unknown) => o.parent.postMessage(m, o.parentOrigin);
  const respond = (requestId: string, r: Pick<EmbedResponse, 'ok' | 'result' | 'error'>) => post({ protocol: EMBED_PROTOCOL, version: EMBED_VERSION, kind: 'response', requestId, ...r });

  const onMessage = (e: MessageEvent) => {
    if (e.origin !== o.parentOrigin) {
      dropped.origin++;
      o.onDrop?.('ORIGIN');
      return;
    }
    if (e.source !== (o.parent as unknown)) {
      dropped.source++;
      o.onDrop?.('SOURCE');
      return;
    }
    const d = e.data as { protocol?: unknown; version?: unknown; requestId?: unknown } | null;
    if (!d || typeof d !== 'object' || d.protocol !== EMBED_PROTOCOL) {
      o.onDrop?.('NOT_PROTOCOL');
      return;
    }
    const rid = RequestId.safeParse(d.requestId);
    if (d.version !== EMBED_VERSION) {
      if (rid.success) respond(rid.data, { ok: false, result: null, error: { code: 'UNSUPPORTED_VERSION', message: `supported version: ${EMBED_VERSION}` } });
      return;
    }
    const cmd = EmbedCommand.safeParse(d);
    if (!cmd.success) {
      if (rid.success) respond(rid.data, { ok: false, result: null, error: { code: 'INVALID_MESSAGE', message: 'message does not match the v1 schema' } });
      return;
    }
    if (!bucket.take()) {
      respond(cmd.data.requestId, { ok: false, result: null, error: { code: 'RATE_LIMITED', message: 'too many commands' } });
      return;
    }
    const c = cmd.data;
    const handler = o.handlers[c.command] as (a: unknown) => Promise<GameState | null> | GameState | null;
    Promise.resolve()
      .then(() => handler('args' in c ? c.args : undefined))
      .then(
        (result) => respond(c.requestId, { ok: true, result: result ?? null, error: null }),
        (err: unknown) => respond(c.requestId, { ok: false, result: null, error: { code: 'COMMAND_FAILED', message: String(err instanceof Error ? err.message : err).slice(0, 256) } }),
      );
  };
  o.self.addEventListener('message', onMessage);
  return {
    emit: (event, data) => post({ protocol: EMBED_PROTOCOL, version: EMBED_VERSION, kind: 'event', seq: ++seq, event, data }),
    dispose: () => o.self.removeEventListener('message', onMessage),
    dropped,
  };
}
